/**
 * LA TARJETA DE UNA CIFRA DEL DÍA.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ NO SE REUSA `Metrica` Y SE HACE UNA AQUÍ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `Metrica` es la ficha genérica que usan Reportes, Caja y media docena de
 * pantallas más. Estas cuatro de la portada tienen un trabajo distinto: son lo
 * primero que ve el dueño al abrir, y la pregunta que contestan no es «¿cuánto?»
 * sino «¿cómo voy?».
 *
 * Eso pide una pieza más: la **comparación**. Un «$47.365» solo no dice nada;
 * un «$47.365, un 7 % más que ayer» sí. Meterle esa pieza a `Metrica` la
 * cargaría para todas las pantallas donde no hace falta.
 *
 * ⚠️ LA COMPARACIÓN SE OMITE CUANDO MIENTE. A las ocho de la mañana, «−100 %
 * vs ayer» es matemáticamente cierto e inútil: el negocio no ha abierto. Un
 * panel que alarma sin motivo es un panel que se deja de mirar.
 */

import type { ReactNode } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { cx } from "@/components/ui";
import { formatPercent, toNumber } from "@/lib/money";

export type TonoCifra = "neutro" | "exito" | "peligro";

export function CifraDelDia({
  rotulo,
  valor,
  icono,
  tono = "neutro",
  variacion,
  contra = "ayer",
  detalle,
}: {
  rotulo: string;
  valor: string;
  icono: ReactNode;
  tono?: TonoCifra;
  /** El porcentaje de cambio. `null` o vacío = no se enseña nada. */
  variacion?: string | null;
  /** Contra qué se compara. Sale escrito: «vs ayer». */
  contra?: string;
  /** Una línea más de contexto, debajo de la comparación. */
  detalle?: ReactNode;
}) {
  const color: Record<TonoCifra, string> = {
    neutro: "text-ink",
    exito: "text-success",
    peligro: "text-danger",
  };

  const pct = variacion == null ? null : toNumber(variacion);
  const subio = pct !== null && pct >= 0;

  return (
    <div className="rounded-[var(--radius-card)] border border-line bg-surface p-3.5 shadow-[var(--shadow-card)] sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <p className="rotulo">{rotulo}</p>
        {/*
          El icono va en su propio recuadro y no suelto. Suelto se confundía con
          el borde de la tarjeta y parecía un adorno mal puesto; encerrado se
          lee como una etiqueta de la sección.
        */}
        <span className="flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-surface-2 text-muted">
          {icono}
        </span>
      </div>

      <p className={cx("mt-3 truncate text-2xl font-semibold tracking-[-0.02em] sm:text-3xl", color[tono])}>
        {valor}
      </p>

      {pct !== null && (
        <span
          className={cx(
            "mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold",
            subio ? "bg-success-soft text-success" : "bg-danger-soft text-danger",
          )}
        >
          {subio ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          {subio ? "+" : "−"}
          {formatPercent(Math.abs(pct))}
          <span className="font-normal opacity-80">vs {contra}</span>
        </span>
      )}

      {detalle && (
        <div className={cx("text-xs leading-relaxed text-muted", pct !== null ? "mt-2" : "mt-2.5")}>
          {detalle}
        </div>
      )}
    </div>
  );
}
